import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./FilterModal.css"; // kompakter/dunkler Akzent

export default function FilterModal({ open, initial, onClose, onApply, onClear, customerTypes = [], contactMethods = [], owners = [], allTags = [] }) {
  const dialogRef = useRef(null);
  const tagInputRef = useRef(null);
  
  const [filters, setFilters] = useState({
    hasEmail: "both",
    hasPhone: "both",
    customerTypes: [], // Array von IDs
    contactMethods: [], // Array von IDs
    contactOwners: [], // Array von IDs
    tags: [], // Array von Tag-Namen
    hasEvent: "both",
    canContact: "both",
  });
  
  const [tagSearch, setTagSearch] = useState("");
  const [showTagDropdown, setShowTagDropdown] = useState(false);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, ...(initial || {}) }));
  }, [initial, open]);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (open) document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleMultiselect = (field, value) => {
    setFilters(f => {
      const arr = f[field] || [];
      if (arr.includes(value)) {
        return { ...f, [field]: arr.filter(v => v !== value) };
      } else {
        return { ...f, [field]: [...arr, value] };
      }
    });
  };

  const apply = (e) => {
    e.preventDefault();
    onApply(filters);
    onClose();
  };

  const clear = () => {
    const empty = { 
      hasEmail: "both", 
      hasPhone: "both", 
      customerTypes: [], 
      contactMethods: [], 
      contactOwners: [], 
      tags: [], 
      hasEvent: "both", 
      canContact: "both" 
    };
    setFilters(empty);
    setTagSearch("");
    onClear?.();
  };
  
  // Filtriere verfügbare Tags basierend auf Sucheingabe
  const availableTags = allTags.filter(tag => {
    const tagLabel = tag.label || tag.name || tag;
    const searchLower = tagSearch.toLowerCase();
    const isAlreadySelected = filters.tags.includes(tagLabel);
    return !isAlreadySelected && tagLabel.toLowerCase().includes(searchLower);
  });
  
  const selectTag = (tag) => {
    const tagLabel = tag.label || tag.name || tag;
    if (!filters.tags.includes(tagLabel)) {
      toggleMultiselect('tags', tagLabel);
    }
    setTagSearch("");
    setShowTagDropdown(false);
  };

  return ReactDOM.createPortal(
    <div
      className="filter-modal-overlay"
      id="filter-modal-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className="filter-modal"
        id="filter-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-title"
        ref={dialogRef}
      >
        <div className="filter-modal-header">
          <h2 id="filter-title">Filter</h2>
          <button
            className="filter-icon-button"
            id="filter-close"
            onClick={onClose}
            aria-label="Schließen"
            type="button"
          >
            ×
          </button>
        </div>

        <form className="filter-modal-body" id="filter-form" onSubmit={apply}>
          <div className="filter-grid-2">
            <label className="filter-label" htmlFor="filter-hasEmail">
              E-Mail
              <select
                id="filter-hasEmail"
                name="hasEmail"
                className="filter-select"
                value={filters.hasEmail}
                onChange={update}
              >
                <option value="both">Beides</option>
                <option value="true">Eingetragen</option>
                <option value="false">Keine</option>
              </select>
            </label>

            <label className="filter-label" htmlFor="filter-hasPhone">
              Telefon
              <select
                id="filter-hasPhone"
                name="hasPhone"
                className="filter-select"
                value={filters.hasPhone}
                onChange={update}
              >
                <option value="both">Beides</option>
                <option value="true">Eingetragen</option>
                <option value="false">Keine</option>
              </select>
            </label>
          </div>

          <label className="filter-label" htmlFor="filter-customerType">
            Kundentyp ({customerTypes.length} verfügbar)
            <div className="filter-multiselect">
              {customerTypes && customerTypes.length > 0 ? (
                customerTypes.map(ct => (
                  <label key={ct.id} className="filter-checkbox-inline">
                    <input
                      type="checkbox"
                      checked={filters.customerTypes.includes(ct.id)}
                      onChange={() => toggleMultiselect('customerTypes', ct.id)}
                    />
                    {ct.type}
                  </label>
                ))
              ) : (
                <p>Keine Kundentypen verfügbar</p>
              )}
            </div>
          </label>

          <label className="filter-label" htmlFor="filter-contactMethod">
            Kontaktmethode ({contactMethods.length} verfügbar)
            <div className="filter-multiselect">
              {contactMethods && contactMethods.length > 0 ? (
                contactMethods.map(cm => (
                  <label key={cm.id} className="filter-checkbox-inline">
                    <input
                      type="checkbox"
                      checked={filters.contactMethods.includes(cm.id)}
                      onChange={() => toggleMultiselect('contactMethods', cm.id)}
                    />
                    {cm.method}
                  </label>
                ))
              ) : (
                <p>Keine Kontaktmethoden verfügbar</p>
              )}
            </div>
          </label>

          <label className="filter-label" htmlFor="filter-contactOwner">
            Zuständiger ({owners.length} verfügbar)
            <div className="filter-multiselect">
              {owners && owners.length > 0 ? (
                owners.map(owner => (
                  <label key={owner.id} className="filter-checkbox-inline">
                    <input
                      type="checkbox"
                      checked={filters.contactOwners.includes(owner.id)}
                      onChange={() => toggleMultiselect('contactOwners', owner.id)}
                    />
                    {owner.username}
                  </label>
                ))
              ) : (
                <p>Keine Zuständigen verfügbar</p>
              )}
            </div>
          </label>

          <label className="filter-label" htmlFor="filter-tag">
            Tags
            <div className="filter-tag-input" style={{ position: 'relative' }}>
              <input
                ref={tagInputRef}
                id="filter-tag"
                name="tempTag"
                className="filter-input"
                placeholder="Tag suchen..."
                value={tagSearch}
                onChange={(e) => {
                  setTagSearch(e.target.value);
                  setShowTagDropdown(true);
                }}
                onFocus={() => setShowTagDropdown(true)}
                onBlur={() => {
                  // Verzögerung damit Klick auf Dropdown registriert wird
                  setTimeout(() => setShowTagDropdown(false), 200);
                }}
              />
              
              {/* Dropdown mit verfügbaren Tags */}
              {showTagDropdown && tagSearch && availableTags.length > 0 && (
                <div className="filter-tag-dropdown">
                  {availableTags.slice(0, 10).map(tag => {
                    const tagLabel = tag.label || tag.name || tag;
                    return (
                      <div
                        key={tag.id || tagLabel}
                        className="filter-tag-dropdown-item"
                        onMouseDown={() => selectTag(tag)}
                      >
                        {tagLabel}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Ausgewählte Tags als Chips */}
              <div className="filter-tag-list">
                {filters.tags.map(tag => (
                  <span key={tag} className="filter-tag-chip">
                    {tag}
                    <button
                      type="button"
                      className="filter-tag-remove"
                      onClick={() => toggleMultiselect('tags', tag)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </label>

          <div className="filter-grid-2">
            <label className="filter-label" htmlFor="filter-hasEvent">
              Ereignis
              <select
                id="filter-hasEvent"
                name="hasEvent"
                className="filter-select"
                value={filters.hasEvent}
                onChange={update}
              >
                <option value="both">Beides</option>
                <option value="true">Hat Ereignis</option>
                <option value="false">Kein Ereignis</option>
              </select>
            </label>

            <label className="filter-label" htmlFor="filter-canContact">
              Kontaktierbar
              <select
                id="filter-canContact"
                name="canContact"
                className="filter-select"
                value={filters.canContact}
                onChange={update}
              >
                <option value="both">Beides</option>
                <option value="true">Erlaubt</option>
                <option value="false">Nicht erlaubt</option>
              </select>
            </label>
          </div>

          <div className="filter-modal-footer">
            <button type="button" className="filter-btn secondary" id="filter-reset" onClick={clear}>
              Zurücksetzen
            </button>
            <button type="submit" className="filter-btn primary" id="filter-apply">
              Anwenden
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
